import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import get from 'lodash.get';
import scrollIntoView from 'scroll-into-view-if-needed';

import SettingsContext from '../settings-context';
import ScrollContext, { ScrollContextType } from './scroll-context';

type ScrollAnchor = {
  element: any;
  focusOn?: any;
};

const getOffsetTop = (element) => {
  let e = element;
  let offsetTop = 0;
  while (e) {
    offsetTop += e.offsetTop;
    e = e.offsetParent;
  }
  return offsetTop;
};

function calculateAnchors(context: ScrollContextType): ScrollAnchor[] {
  const anchors: ScrollAnchor[] = context.anchors
    .filter((a) => a.aRef.current)
    .map((a) => ({
      element: a.aRef.current,
      focusOn: a.focusOn?.current,
    }));
  const anchorOffsets = new Map<any, number>(
    anchors.map((a) => [a.element, getOffsetTop(a.element)]),
  );
  anchors.sort(
    (a1, a2) => anchorOffsets.get(a1.element) - anchorOffsets.get(a2.element),
  );
  return anchors;
}

export default function useScroller() {
  const context = useContext(ScrollContext);
  const formContext = useFormContext();
  const updatableFormContext = useRef(formContext);
  const { disableAutoFocus } = useContext(SettingsContext);

  useEffect(() => {
    updatableFormContext.current = formContext;
  }, [formContext]);

  const scrollToSuccessor = useCallback(
    (id, successorOffset) => {
      if (context.scrollMode === 'disabled') {
        return;
      }
      const anchors = calculateAnchors(context);
      const currentAnchorIndex = anchors.findIndex(
        (a) => a.element.id === `scroll-anchor-${id}`,
      );
      if (currentAnchorIndex > -1) {
        const successor = anchors[currentAnchorIndex + successorOffset];
        const thisAnchor = anchors[currentAnchorIndex];
        thisAnchor.focusOn?.blur();
        if (successor) {
          scrollIntoView(successor.element, {
            behavior: 'smooth',
            block: 'start',
          });
          if (successor.focusOn && !disableAutoFocus) {
            setTimeout(() => successor.focusOn.focus(), 1000);
          }
        }
      }
    },
    [context, disableAutoFocus],
  );

  const scrollToSelf = useCallback(
    (id) => {
      if (context.scrollMode === 'disabled') {
        return;
      }
      const anchors = calculateAnchors(context);
      const thisAnchor = anchors.find(
        (a) => a.element.id === `scroll-anchor-${id}`,
      );
      if (thisAnchor) {
        scrollIntoView(thisAnchor.element, {
          behavior: 'smooth',
          block: 'start',
        });
        if (thisAnchor.focusOn && !disableAutoFocus) {
          setTimeout(() => thisAnchor.focusOn.focus(), 1000);
        }
      }
    },
    [context, disableAutoFocus],
  );

  const scrollToError = useCallback(
    (id = null) =>
      setTimeout(() => {
        const errors = updatableFormContext?.current?.errors;
        const anchors = calculateAnchors(context);
        for (let i = 0; i < anchors.length; i += 1) {
          const anchor = anchors[i];
          const anchorId = anchor.element.id.replace(/^scroll-anchor-/, '');
          const anchorError = get(errors, anchorId);
          if (anchorError && (!id || anchorId === id)) {
            scrollIntoView(anchor.element, {
              behavior: 'smooth',
              block: 'start',
            });
            if (anchor.focusOn && !disableAutoFocus) {
              setTimeout(() => anchor.focusOn.focus(), 1000);
            }
            break;
          }
        }
      }),
    [context, updatableFormContext, disableAutoFocus],
  );

  return useMemo(
    () => ({ scrollToSuccessor, scrollToSelf, scrollToError }),
    [scrollToSuccessor, scrollToSelf, scrollToError],
  );
}
